import { useNode, useEditor } from "@craftjs/core";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useMemo, useState, useEffect } from "react";
import { GrigoraDashboardContext } from "../../dashboard/context";
import {
  UnitInput,
  ToggleInput,
  BoxShadowInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  CSSFilters,
  RichTextFormatting,
  SelectInput,
  TabsV2,
  RangeNumberInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import {
  changeTransition,
  isUpdatedFromDefault,
  getDirectoryInfoFromParents,
  getDirectoryFieldOptions,
} from "../../utils";
import { ImagePickerUIKit } from "../../ui-kit";
import { INPUT_LIMITS } from "../../input-limits";
import SourceImage from "../../ui-kit/source-image";
import { remove_prefix } from "../../helpers";
import { GrigoraEditorContext } from "../../editor/context";

export const ImageSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
  } = useEditor();

  const parents = node(id).ancestors(true);

  const { projectID, directoryTypes } = useContext(GrigoraDashboardContext);
  const { pagesData, currentPage } = useContext(GrigoraEditorContext) ?? {};

  // Effect to auto-select directory source when appropriate
  useEffect(() => {
    function findImageField(directoryTypeId) {
      var allFields = directoryTypes?.find(
        (type) => remove_prefix(type.sk, "directory-type-") === directoryTypeId,
      )?.fields;

      var imageFields = allFields?.filter((field) => field.type === "image");
      if (imageFields?.length > 0) {
        return imageFields[0].id;
      }

      return null;
    }

    if (props.isSrcDynamic && !props.dynamicSrc) {
      const directoryInfo = getDirectoryInfoFromParents(parents, node);
      if (directoryInfo) {
        setProp((props) => {
          props.dynamicSrc = "directory-listing";
          props.dynamicSrcData = {
            directory_type_id: directoryInfo.directoryTypeId,
            directory_field: findImageField(directoryInfo.directoryTypeId),
          };
        });
      } else {
        // Check if current page is a directory listing page
        var currentPageData = pagesData?.[currentPage] ?? {};

        if (
          currentPageData?.type === "template" &&
          currentPageData?.template === "directory-template" &&
          currentPageData?.directory_id
        ) {
          var pageDirectoryId = currentPageData?.directory_id;

          var directoryType = directoryTypes?.find(
            (type) =>
              remove_prefix(type.sk, "directory-type-") === pageDirectoryId,
          );

          if (directoryType) {
            setProp((props) => {
              props.dynamicSrc = "directory-listing";
              props.dynamicSrcData = {
                directory_type_id: pageDirectoryId,
                directory_field_id: findImageField(pageDirectoryId),
              };
            });
          }
        }
      }
    }
  }, [props.isSrcDynamic]);

  const {
    isSrcDynamic,
    dynamicSrc,
    dynamicSrcData,
    caption,
    src,
    srcMode,
    width,
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    align,
    alignTablet,
    alignMobile,
    lazyLoad = true,
    altText,
    titleText,
    boxShadow,
    border,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    borderRadius,
    cssFilter,
    hoverBoxShadow,
    hoverBorder,
    hoverBorderRadius,
    hoverLayoutPadding,
    hoverLayoutPaddingTablet,
    hoverLayoutPaddingMobile,
    hoverLayoutMargin,
    hoverLayoutMarginTablet,
    hoverLayoutMarginMobile,
    transition,
  } = props;

  const [srcModal, setSrcModal] = useState(false);

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          {isSrcDynamic ? (
            <></>
          ) : (
            <>
              <div className="flex items-center justify-center text-white cursor-pointer text-sm bg-gray-200 duration-300 hover:bg-editor-bg-dark mb-4">
                <SourceImage
                  projectID={projectID}
                  src={src}
                  srcMode={srcMode}
                  onClick={() => {
                    setSrcModal(true);
                  }}
                />
              </div>
            </>
          )}
          <ImagePickerUIKit
            src={src}
            srcMode={srcMode}
            onChange={(src, srcMode) => {
              setProp((props) => {
                props.src = src;
                props.srcMode = srcMode;
              });
            }}
            opened={srcModal}
            setOpened={setSrcModal}
          />
          <ToggleInput
            isResetVisible={false}
            label="Is Image Dynamic?"
            value={isSrcDynamic}
            onChange={(val) => {
              setProp((props) => {
                props.isSrcDynamic = val;
              });
            }}
          />
          {isSrcDynamic ? (
            <>
              <SelectInput
                isResetVisible={false}
                label="Source"
                value={dynamicSrc}
                onChange={(val) => {
                  setProp((props) => {
                    props.dynamicSrc = val;
                    if (val !== "directory-listing") {
                      props.dynamicSrcData = undefined;
                    }
                  });
                }}
                options={[
                  {
                    label: "-",
                    value: "",
                  },
                  {
                    label: "Post Featured Image",
                    value: "post-featured-image",
                  },
                  {
                    label: "Category Featured Image",
                    value: "category-featured-image",
                  },
                  {
                    label: "Directory Listing",
                    value: "directory-listing",
                  },
                ]}
              />
              {dynamicSrc === "directory-listing" &&
                (directoryTypes?.length > 0 ? (
                  <>
                    <SelectInput
                      isResetVisible={false}
                      label="Directory"
                      value={dynamicSrcData?.directory_type_id}
                      onChange={(val) => {
                        setProp((props) => {
                          props.dynamicSrcData = {
                            ...(props.dynamicSrcData ?? {}),
                            directory_type_id: val,
                          };
                        });
                      }}
                      options={[
                        {
                          label: "Select Directory",
                          value: "",
                        },
                        ...directoryTypes?.map((type) => ({
                          label: type.name,
                          value: remove_prefix(type.sk, "directory-type-"),
                        })),
                      ]}
                    />
                    {dynamicSrcData?.directory_type_id && (
                      <SelectInput
                        isResetVisible={false}
                        label="Field"
                        value={dynamicSrcData?.directory_field_id}
                        onChange={(val) => {
                          setProp((props) => {
                            props.dynamicSrcData = {
                              ...(props.dynamicSrcData ?? {}),
                              directory_field_id: val,
                            };
                          });
                        }}
                        options={getDirectoryFieldOptions(
                          directoryTypes,
                          dynamicSrcData?.directory_type_id,
                          ["image"],
                        )}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      No directory types found
                    </span>
                  </>
                ))}
            </>
          ) : (
            <></>
          )}
          <UnitInput
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            resetValue={undefined}
            value={width}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            resetValue={undefined}
            value={height}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
          <AlignInput
            resetValue={undefined}
            value={align}
            isResponsive={true}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <TextInput
            minLength={INPUT_LIMITS["image-alt-text"].minlength}
            maxLength={INPUT_LIMITS["image-alt-text"].maxlength}
            resetValue={undefined}
            label={"Alternate Text"}
            value={altText}
            onChange={(val) => {
              setProp((props) => {
                props.altText = val;
              });
            }}
          />
          <TextInput
            minLength={INPUT_LIMITS["image-caption"].minlength}
            maxLength={INPUT_LIMITS["image-caption"].maxlength}
            resetValue={undefined}
            label={"Caption Text"}
            value={caption}
            onChange={(val) => {
              setProp((props) => {
                props.caption = val;
              });
            }}
          />
          <TextInput
            minLength={INPUT_LIMITS["image-title"].minlength}
            maxLength={INPUT_LIMITS["image-title"].maxlength}
            resetValue={undefined}
            label={"Title Text"}
            value={titleText}
            onChange={(val) => {
              setProp((props) => {
                props.titleText = val;
              });
            }}
          />
          <ToggleInput
            resetValue={true}
            label="Lazy load"
            value={lazyLoad}
            onChange={(val) => {
              setProp((props) => {
                props.lazyLoad = val;
              });
            }}
          />
        </Spacer>
        <RichTextFormatting />
      </div>
    );
  }

  function AdvancedSettings() {
    const boxShadowTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverSettings(),
      },
    ];
    function normalSettings() {
      return (
        <>
          <BoxShadowInput
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </>
      );
    }
    function hoverSettings() {
      return (
        <>
          <BoxShadowInput
            value={hoverBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBoxShadow = val;
              });
            }}
          />

          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["box-shadow"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["box-shadow"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const layoutTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalLayoutSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverLayoutSettings(),
      },
    ];
    function normalLayoutSettings() {
      return (
        <>
          <MarginInput
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />

          <PaddingInput
            isResponsive={true}
            value={layoutPadding}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </>
      );
    }
    function hoverLayoutSettings() {
      return (
        <>
          <MarginInput
            isResponsive={true}
            valueTablet={hoverLayoutMarginTablet}
            valueMobile={hoverLayoutMarginMobile}
            value={hoverLayoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLayoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverLayoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverLayoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />

          <PaddingInput
            isResponsive={true}
            value={hoverLayoutPadding}
            valueTablet={hoverLayoutPaddingTablet}
            valueMobile={hoverLayoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLayoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverLayoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverLayoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("margin", "padding")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["margin", "padding"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    const borderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBorderSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBorderSettings(),
      },
    ];
    function normalBorderSettings() {
      return (
        <>
          <BorderInput
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </>
      );
    }
    function hoverBorderSettings() {
      return (
        <>
          <BorderInput
            value={hoverBorder}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            value={hoverBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />

          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["border"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["border", "border-radius"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutMargin },
            { key: layoutPadding },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
            { key: hoverLayoutMarginTablet },
            { key: hoverLayoutMarginMobile },
            { key: hoverLayoutMargin },
            { key: hoverLayoutPadding },
            { key: hoverLayoutPaddingTablet },
            { key: hoverLayoutPaddingMobile },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <TabsV2 items={layoutTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
            { key: hoverBorder },
            { key: hoverBorderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <TabsV2 items={borderTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: hoverBoxShadow },
            { key: boxShadow },
          ])}
          id={`block-setting-${id}-shadows`}
          title={"Shadows"}
          initialOpen={false}
        >
          <TabsV2 items={boxShadowTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: cssFilter }])}
          id={`block-setting-${id}-css-filters`}
          title={"CSS Filters"}
          initialOpen={false}
        >
          <CSSFilters
            label="CSS Filters"
            value={cssFilter}
            onChange={(val) => {
              setProp((props) => {
                props.cssFilter = val;
              });
            }}
          ></CSSFilters>
        </Panel>

        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: projectID },
        { key: src },
        { key: srcMode },
        { key: isSrcDynamic },
        { key: dynamicSrc },
        { key: widthTablet },
        { key: widthMobile },
        { key: width },
        { key: heightTablet },
        { key: heightMobile },
        { key: height },
        { key: align },
        { key: alignMobile },
        { key: alignTablet },
        { key: altText },
        { key: titleText },
        { key: lazyLoad },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: border },
        { key: cssFilter },
        { key: borderRadius },
        { key: boxShadow },
        { key: hoverBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
